import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { SkeletonLoader } from '@grandvisionhq/elements';
import { Alert } from '@grandvisionhq/iris-ui';
import { useIntl } from '@grandvisionhq/state';
import { FormContext } from '../Form/Form.js';
import FormControlFieldWrapper from '../FormControl/FormControlWrapper.js';
import { usePhoneNumberInputPackage } from '../Util/react-phone-number-input/use-phone-number-input-package.js';
import '../Util/react-phone-number-input/get-parse-phone-number.js';
import { maxLength } from '../Util/validations.js';
import PhoneNumberInput from './PhoneNumberInput.js';

var _value, _value1;
// based on FormControl
const initialStateAdapter = (initialState, id, validate)=>{
    return {
        value: (_value = initialState.value) !== null && _value !== void 0 ? _value : '',
        isTouched: !!(initialState === null || initialState === void 0 ? void 0 : initialState.isTouched),
        isValid: validate((_value1 = initialState.value) !== null && _value1 !== void 0 ? _value1 : ''),
        readonly: !!(initialState === null || initialState === void 0 ? void 0 : initialState.readonly),
        name: id
    };
};
var PhoneNumberInputContainer = (_param)=>{
    var { defaultCountry , overrideTranslations , allowlist , countriesOrder , placeholder , id , onBlur , initialState , onChange , required , maxLength: maxLength$1 , errorMessage , className , label , tooltip , optionalText , disabled , additionalValidations , localeForTranslations ='en' , hideAsteriskSign =false , PhoneNumberInputModule  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "defaultCountry",
        "overrideTranslations",
        "allowlist",
        "countriesOrder",
        "placeholder",
        "id",
        "onBlur",
        "initialState",
        "onChange",
        "required",
        "maxLength",
        "errorMessage",
        "className",
        "label",
        "tooltip",
        "optionalText",
        "disabled",
        "additionalValidations",
        "localeForTranslations",
        "hideAsteriskSign",
        "PhoneNumberInputModule"
    ]);
    const { getCountries , isValidPhoneNumber: isValidPhoneNumber1 , parsePhoneNumber  } = PhoneNumberInputModule;
    const executedOnMount = React.useRef(false);
    React.useEffect(()=>{
        if (allowlist) {
            if (!allowlist.includes(defaultCountry)) {
                throw new Error('defaultCountry must be included in allowlisted countries');
            }
        }
    }, [
        allowlist,
        defaultCountry
    ]);
    const translations = React.useMemo(()=>{
        if (!localeForTranslations) {
            return overrideTranslations;
        }
        const countryNamesForLocale = new Intl.DisplayNames([
            localeForTranslations
        ], {
            type: 'region'
        });
        const baseTranslations = getCountries().reduce((acc, countryCode)=>swcHelpers.objectSpread({}, acc, {
                [countryCode]: countryNamesForLocale.of(countryCode)
            })
        , {});
        return swcHelpers.objectSpread({}, baseTranslations, overrideTranslations);
    }, [
        getCountries,
        localeForTranslations,
        overrideTranslations
    ]);
    const validate = React.useCallback((value)=>{
        if (!required && value === '') {
            return true;
        }
        if (maxLength$1 && !maxLength(maxLength$1)(value)) {
            return false;
        }
        const isValidNumber = isValidPhoneNumber1(value);
        return additionalValidations ? additionalValidations.reduce((isValid, validator)=>validator(value) && isValid
        , isValidNumber) : isValidNumber;
    }, [
        required,
        maxLength$1,
        isValidPhoneNumber1,
        additionalValidations
    ]);
    const [phoneState, setPhoneState] = React.useState(()=>initialStateAdapter(initialState, id, validate)
    );
    const handleOnChange = React.useCallback((formState)=>{
        setPhoneState(formState);
        // if component is connected update formState as well
        onChange === null || onChange === void 0 ? void 0 : onChange(formState);
    }, [
        onChange
    ]);
    React.useEffect(()=>{
        if (executedOnMount.current === false) {
            executedOnMount.current = true;
            /* We need to call onChange once on mount so our field is registered in our form context.
      This is needed for example for our component to be touched by form context in case the form
      is submitted but the user hasn't touched the field. (so our error overlay will show up in
      case this field is required) */ handleOnChange({
                value: phoneState.value,
                name: id,
                errorMessage,
                isValid: validate(phoneState.value),
                readonly: phoneState.readonly
            });
        }
    }, [
        handleOnChange,
        id,
        phoneState.readonly,
        phoneState.value,
        validate
    ]);
    const updateState = React.useCallback(()=>{
        setPhoneState((prev)=>{
            const state = swcHelpers.objectSpread({}, prev, initialStateAdapter(initialState, id, validate), {
                // if element isTouched already, it shouldn't change back to false
                isTouched: prev.isTouched ? prev.isTouched : !!initialState.isTouched
            });
            /* handle initialState changing value on every
          render whenever form context state is updated */ if (JSON.stringify(prev) === JSON.stringify(state)) {
                return prev;
            } else {
                let savedPhoneNumber = state.value;
                let containsCountryCode = savedPhoneNumber.includes('+');
                let parsedPhoneNumber = parsePhoneNumber(savedPhoneNumber);
                if (containsCountryCode && allowlist) {
                    const countryOfSavedPhoneNumber = parsedPhoneNumber === null || parsedPhoneNumber === void 0 ? void 0 : parsedPhoneNumber.country;
                    if (!countryOfSavedPhoneNumber || !allowlist.includes(countryOfSavedPhoneNumber)) {
                        savedPhoneNumber = '';
                        containsCountryCode = false;
                    }
                }
                /* If user has a saved phone number that doesn't include a
            country code, append the default country's code */ if (!containsCountryCode) {
                    parsedPhoneNumber = parsePhoneNumber(savedPhoneNumber, defaultCountry);
                }
                /* If our saved phone number is invalid, we instead
            auto completing with empty string instead */ const isValidPhoneNumber = parsedPhoneNumber && validate(parsedPhoneNumber.number);
                const newValue = parsedPhoneNumber && isValidPhoneNumber ? parsedPhoneNumber.number : '';
                const newState = swcHelpers.objectSpread({}, state, {
                    value: newValue,
                    // validate again in case this is an empty string
                    isValid: validate(newValue)
                });
                // fix changing flag getting blocked by not needed form state update
                if (!(initialState.value === '' && newValue === '')) {
                    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
                }
                return newState;
            }
        });
    }, [
        allowlist,
        defaultCountry,
        id,
        initialState,
        onChange,
        parsePhoneNumber,
        validate
    ]);
    React.useEffect(()=>{
        updateState();
    }, [
        updateState
    ]);
    const handleOnBlur = React.useCallback(()=>{
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(phoneState);
        /* show validation after user left the field initially and then
      validate on character change like other form control fields */ handleOnChange(swcHelpers.objectSpread({}, phoneState, {
            name: id,
            isTouched: true,
            isValid: validate(phoneState.value)
        }));
    }, [
        handleOnChange,
        onBlur,
        phoneState,
        id,
        validate
    ]);
    const { isTouched , readonly , isValid: isValid1  } = phoneState;
    const displayValid = isValid1 === true && isTouched === true && phoneState.value !== '' && !readonly;
    const displayError = isValid1 === false && isTouched === true && !readonly;
    const errorMessageId = `${id}-error-message`;
    const ariaDescribedBy = `${rest['aria-describedby'] || ''} ${errorMessageId}`.trim();
    const inputClasses = classnames('form-control__input', className, {
        '_phone-input': true,
        _is_valid: displayValid,
        _is_invalid: displayError
    });
    const onChangeValueOnly = React.useCallback((value)=>{
        handleOnChange(swcHelpers.objectSpread({}, phoneState, {
            value: value !== null && value !== void 0 ? value : '',
            isValid: validate(value !== null && value !== void 0 ? value : '')
        }));
    }, [
        handleOnChange,
        phoneState,
        validate
    ]);
    return /*#__PURE__*/ React.createElement(PhoneNumberInput, swcHelpers.extends({}, swcHelpers.objectSpread({
        defaultCountry,
        labels: translations,
        countries: allowlist,
        countryOptionsOrder: countriesOrder,
        /* do not display the +${countrycode} when a value
            that is set contains it since its already shown in svg*/ initialValueFormat: 'national',
        flagUrl: 'https://assets.grandvision.io/global/images/flags/{XX}+Code.svg',
        international: false,
        addInternationalOption: false,
        placeholder,
        id,
        required,
        formControlWrapperProps: {
            className,
            label,
            hideAsteriskSign,
            tooltip,
            optionalText,
            errorMessageId,
            errorMessage,
            displayError
        },
        maxLength: maxLength$1,
        tooltip,
        disabled: disabled || phoneState.readonly,
        value: phoneState.value,
        inputClasses,
        name: phoneState.name,
        onChange: onChangeValueOnly,
        onBlur: handleOnBlur,
        'aria-invalid': !isValid1,
        'aria-required': required
    }, rest, {
        'aria-describedby': ariaDescribedBy,
        PhoneNumberInputComponent: PhoneNumberInputModule.default
    })));
};
var ConnectedPhoneNumberInput = (_param)=>{
    var { onBlur , onChange  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "onBlur",
        "onChange"
    ]);
    return /*#__PURE__*/ React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(PhoneNumberInputContainer, swcHelpers.extends({}, rest, {
            onBlur: context.onBlur || onBlur,
            onChange: context.onChange || onChange,
            initialState: context.getInitialState && context.getInitialState(rest.id) || rest.initialState && rest.initialState
        }))
    );
};
const LazyPhoneNumberInput = (props)=>{
    const [module, loading, error] = usePhoneNumberInputPackage();
    const { getLabel  } = useIntl();
    const FormControlWrapperProps = {
        className: props.className,
        label: props.label,
        hideAsteriskSign: props.hideAsteriskSign,
        tooltip: props.tooltip,
        optionalText: props.optionalText,
        errorMessageId: `${props.id}-error-message`,
        errorMessage: props.errorMessage,
        displayError: false
    };
    if (loading) {
        return /*#__PURE__*/ React.createElement(FormControlFieldWrapper, swcHelpers.extends({}, FormControlWrapperProps, {
            id: props.id,
            required: props.required
        }), /*#__PURE__*/ React.createElement(SkeletonLoader, null, /*#__PURE__*/ React.createElement("svg", {
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            height: "50px",
            "aria-label": getLabel('common.loadingText'),
            className: "phone-number-input__skeleton-loader"
        }, /*#__PURE__*/ React.createElement("rect", {
            width: "100%",
            height: "50px",
            rx: "5",
            fill: "currentColor"
        }))));
    }
    if (!module || error) {
        return /*#__PURE__*/ React.createElement(FormControlFieldWrapper, swcHelpers.extends({}, FormControlWrapperProps, {
            id: props.id,
            required: props.required
        }), /*#__PURE__*/ React.createElement(Alert, {
            variant: "error",
            className: "alert-m-default"
        }, getLabel('common.error.network')));
    }
    return /*#__PURE__*/ React.createElement(ConnectedPhoneNumberInput, swcHelpers.extends({}, props, {
        PhoneNumberInputModule: module
    }));
};

export { ConnectedPhoneNumberInput, PhoneNumberInputContainer, LazyPhoneNumberInput as default };
//# sourceMappingURL=PhoneNumberInputContainer.js.map
